import { useState } from "react";
import { supabase } from "../lib/supabaseClient";
import { getToken } from "firebase/messaging";
import { messaging } from "../lib/firebase";

type Props = {
  profile: any;
  onUpdate: (field: string, value: boolean) => void;
  savingField: string | null;
};

export default function PushSettingsSection({
  profile,
  onUpdate,
  savingField,
}: Props) {
  const [pushStatus, setPushStatus] = useState<
    "idle" | "success" | "error" | "loading"
  >("idle");

  const handlePushReRegister = async () => {
    setPushStatus("loading");

    try {
      const permission = await Notification.requestPermission();

      if (permission !== "granted") {
        console.warn("🚫 Berechtigung verweigert");
        setPushStatus("error");
        return;
      }

      const registration = await navigator.serviceWorker.register(
        "/firebase-messaging-sw.js"
      );

      const vapidKey =
        "BLBMpj-QGt45qj8nG8OeJS9GDGKk4AThrROZ3FHDQ29HsQPkChOdIo8Hv4V5ouGHIV_FSG7CuOwCgiyFMQz7w6Y";

      const currentToken = await getToken(messaging, {
        vapidKey,
        serviceWorkerRegistration: registration,
      });

      if (!currentToken) {
        console.warn("⚠️ Kein Token erhalten");
        setPushStatus("error");
        return;
      }

      const deviceInfo = `${navigator.userAgent} (${window.innerWidth}x${window.innerHeight})`;

      const { error } = await supabase.from("user_push_tokens").upsert(
        [
          {
            user_id: profile.id,
            token: currentToken,
            platform: "web",
            device_info: deviceInfo,
            updated_at: new Date().toISOString(),
          },
        ],
        { onConflict: "token" }
      );

      if (error) {
        console.error("❌ Fehler beim Upsert:", error);
        setPushStatus("error");
      } else {
        setPushStatus("success");
      }
    } catch (err) {
      console.error("❌ Fehler bei Push Re-Registrierung:", err);
      setPushStatus("error");
    }
  };

  return (
    <div className="bg-[#161b27] border border-[#2a3347] rounded-xl p-4 mt-6">
      <h2 className="text-lg font-semibold mb-2 text-slate-100">📬 Push-Einstellungen</h2>
      <p className="text-sm text-slate-400 mb-4">
        Wähle aus, für welche Ereignisse du Push-Benachrichtigungen erhalten
        möchtest.
      </p>

      <ToggleRow
        label="Bei neuen Aufgaben"
        value={profile.push_for_tasks}
        onChange={(val) => onUpdate("push_for_tasks", val)}
        loading={savingField === "push_for_tasks"}
      />
      <ToggleRow
        label="Bei neuen Nachrichten"
        value={profile.push_for_notifications}
        onChange={(val) => onUpdate("push_for_notifications", val)}
        loading={savingField === "push_for_notifications"}
      />

      <div className="mt-4">
        <button
          onClick={handlePushReRegister}
          className="px-4 py-2 bg-sky-500 text-white rounded hover:bg-sky-600 disabled:opacity-50"
          disabled={pushStatus === "loading"}
        >
          🔄 Push erneut aktivieren
        </button>

        {pushStatus === "success" && (
          <p className="text-emerald-400 mt-2 text-sm">
            ✅ Push-Token erfolgreich gespeichert.
          </p>
        )}
        {pushStatus === "error" && (
          <p className="text-red-400 mt-2 text-sm">
            ❌ Fehler beim Speichern des Push-Tokens.
          </p>
        )}
      </div>
    </div>
  );
}

function ToggleRow({
  label,
  value,
  onChange,
  loading,
}: {
  label: string;
  value: boolean;
  onChange: (val: boolean) => void;
  loading: boolean;
}) {
  return (
    <label className="flex justify-between items-center mb-3">
      <span className="text-slate-200">{label}</span>
      <input
        type="checkbox"
        checked={value}
        onChange={(e) => onChange(e.target.checked)}
        disabled={loading}
        className="w-5 h-5"
      />
    </label>
  );
}
